import { supabase } from "@/integrations/supabase/client";

export interface VideoGenerationRequest {
  text: string;
  avatar_id?: string;
  voice_id?: string;
  speed?: number;
  pitch?: number;
  width?: number;
  height?: number;
  user_id?: string;
  captions_enabled?: boolean;
  background_image_url?: string;
}

export interface VideoGenerationJob {
  id: string;
  video_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  video_url?: string;
  thumbnail_url?: string;
  cloudinary_url?: string;
  prompt: string;
  avatar_id?: string;
  voice_id?: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  error?: string;
}

export async function createVideoGeneration(request: VideoGenerationRequest): Promise<string> {
  try {
    // Call edge function to create video
    const { data, error } = await supabase.functions.invoke("heygen-create-video", {
      body: request,
    });

    if (error) {
      const msg = typeof error === 'string' ? error : (error as any)?.message || '';
      if (msg.includes('Function not implemented')) {
        throw new Error(
          "Video generation backend not running. Please deploy or serve Supabase Edge Functions: heygen-create-video, heygen-get-status, heygen-list-resources."
        );
      }
      throw error as any;
    }
    
    const videoId = data?.data?.video_id;
    if (!videoId) throw new Error("No video_id returned from HeyGen");

    // Save to database for tracking
    const { error: dbError } = await supabase.from("video_generations").insert({
      video_id: videoId,
      status: "pending",
      user_id: request.user_id,
      // No longer storing captions/background in DB
    });

    if (dbError) console.error("Failed to save video generation to DB:", dbError);

    return videoId;
  } catch (error: any) {
    console.error("Error creating video generation:", error);
    throw new Error(error.message || "Failed to create video");
  }
}

export async function checkVideoStatus(video_id: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke("heygen-get-status", {
      body: { video_id },
    });

    if (error) throw error;

    const statusData = data?.data;
    
    // Update database with latest status
    if (statusData) {
      const updatePayload: any = {
        status: statusData.status,
        updated_at: new Date().toISOString(),
      };

      if (statusData.video_url) updatePayload.video_url = statusData.video_url;
      if (statusData.thumbnail_url) updatePayload.thumbnail_url = statusData.thumbnail_url;
      if (statusData.error) updatePayload.error = statusData.error;

      // No Cloudinary upload - just use HeyGen URL directly
      await supabase
        .from("video_generations")
        .update(updatePayload)
        .eq("video_id", video_id);
    }

    return statusData;
  } catch (error: any) {
    console.error("Error checking video status:", error);
    throw new Error(error.message || "Failed to check video status");
  }
}

export async function uploadVideoToCloudinary(videoUrl: string, videoId: string): Promise<string> {
  try {
    const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || "dz7sec6ii";
    const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || "oreog_preset";

    // Download video from HeyGen URL
    const videoResponse = await fetch(videoUrl);
    if (!videoResponse.ok) throw new Error("Failed to fetch video from HeyGen");
    
    const videoBlob = await videoResponse.blob();

    // Upload to Cloudinary
    const formData = new FormData();
    formData.append("file", videoBlob);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("public_id", `heygen_videos/${videoId}`);
    formData.append("resource_type", "video");

    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/video/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Cloudinary upload failed: ${errorText}`);
    }

    const uploadData = await uploadResponse.json();
    return uploadData.secure_url;
  } catch (error: any) {
    console.error("Error uploading to Cloudinary:", error);
    throw error;
  }
}

export async function listAvatars(): Promise<any[]> {
  try {
    const { data, error } = await supabase.functions.invoke("heygen-list-resources", {
      body: { type: "avatars" },
    });

    if (error) throw error;
    return data?.data?.avatars || [];
  } catch (error: any) {
    console.error("Error listing avatars:", error);
    return [];
  }
}

export async function listVoices(): Promise<any[]> {
  try {
    const { data, error } = await supabase.functions.invoke("heygen-list-resources", {
      body: { type: "voices" },
    });

    if (error) throw error;
    return data?.data?.voices || [];
  } catch (error: any) {
    console.error("Error listing voices:", error);
    return [];
  }
}

export async function getUserVideoGenerations(userId?: string): Promise<VideoGenerationJob[]> {
  try {
    let query = supabase
      .from("video_generations")
      .select("*")
      .order("created_at", { ascending: false });

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error: any) {
    console.error("Error fetching video generations:", error);
    return [];
  }
}

// Background polling for pending videos
export function startVideoStatusPolling(
  videoId: string,
  onUpdate: (status: any) => void,
  intervalMs: number = 5000
): () => void {
  let intervalId: any;
  let isPolling = true;

  const poll = async () => {
    if (!isPolling) return;

    try {
      const status = await checkVideoStatus(videoId);
      onUpdate(status);

      // Stop polling if completed or failed
      if (status?.status === "completed" || status?.status === "failed") {
        isPolling = false;
        if (intervalId) clearInterval(intervalId);
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Initial check
  poll();

  // Set up interval
  intervalId = setInterval(poll, intervalMs);

  // Return cleanup function
  return () => {
    isPolling = false;
    if (intervalId) clearInterval(intervalId);
  };
}
